import { documentService } from "@/app/providers/services";
import { isLockedByMe, isLockedByOther } from "@/lib/user";
import { useCurrentProject } from "@/common";
import type { SectionData } from "@/common/editor/components/section/section.types";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import { useNotify } from "@/common/notifications";
import { crudGetOne } from "@/legacy/redux";
import { cleanupJSpreadsheetData } from "@/pages/models/release/Documents/DataTable/utils";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import { WORKSPACE_LAYER } from "@/ui/dialogs/layers";
import { graphlib } from "@dagrejs/dagre";
import { useConfirmDialog } from "@imminently/decisively-ui/ConfirmDialog";
import Dialog, { useDialog } from "@imminently/decisively-ui/Dialog";
import type { BackendResource, DeepPartial } from "@imminently/immi-query";
import { uuid } from "@imminently/interview-sdk";
import type {
  Document,
  DocumentContents,
  DocumentError,
  ParsedRuleGraph,
  Release,
  RuleDocContentsV2,
} from "@packages/commons";
import { NAMESPACE_OPT_IN, findReleaseDocument, flattenReleaseDocuments } from "@packages/commons";
import { compile } from "@packages/compiler";
// Note slice in redux toolkit uses immer so we mutate state
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import get from "lodash/get";
import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { createTransform, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { useDebouncedCallback } from "use-debounce";
import * as fullReleaseRedux from "../../redux/fullRelease/reducer";
import { useLockFile } from "./FileExplorer/documents.api";
import { wrapTechnicalCompilerError } from "./compileErrors";
import { applyLocalErrorsToSavedDocument } from "./documentSaveErrors";
import { DESERIALIZED_SYMBOL, type DeserializedDocumentContents } from "./documents.slice.constants";
import { deserializeDocument, serializeContents, serializeDocument } from "./documents.slice.helpers";

export * from "./documents.slice.constants";

// /**
//  * We use this symbol to make sure the types of the deserialized documents are distinct from the serialized ones
//  * This is to prevent accidentally sending a deserialized document to the backend
//  */
// export const DESERIALIZED_SYMBOL = Symbol("deserialized");

// export interface DeserializedRuleSheet {
//   title: string;
//   columns: any[];
//   data: any[];
// }

/**
 * move document edits into editor
 * editor contains documents by id
 * document contents is converted on initial set
 * allows comparison at any time
 * keeps document edits in memory, ie navigating or changing file is not an issue
 *
 * pull the doc from BE store it by id
 * need to store the og document
 * need to store client side contents (converted to slate/plate)
 * need to store errors
 * need to store validations
 * need to store last saved
 */

export interface EditorDocument {
  document: BackendResource<Document>;
  contents: DeserializedDocumentContents;
  errors: DocumentError[];
  validations: any[];
  lastSaved: number | null;
  isDirty: boolean;
  keepLocal: boolean;
  lastLoadedTime: number;
  /**
   * Used to force a re-render of the plate editor.
   * Sometimes we need the editor to re-render, however trying to use other meta information is too aggressive.
   * NOTE forcing a re-render will reset the users scroll position. So use this sparingly.
   */
  renderKey: number;
}

const defaultFrontendSheet = JSON.stringify({
  declarations: [{}],
  sheets: [
    {
      title: "Sheet 1",
      headings: [
        { type: "OPM-Condition Heading", legend: "" },
        { type: "OPM-Conclusion Heading", legend: "" },
      ],
      rows: [
        [
          { type: "OPM-Condition", text: "" },
          { type: "OPM-Conclusion", text: "" },
        ],
        [
          { type: "OPM-Else", text: "else" },
          { type: "OPM-Conclusion", text: "" },
        ],
      ],
    },
  ],
});
const defaultBackendSheet = JSON.stringify({ declarations: [], sheets: [] });

// const compareDocuments = (a: Document, b: Document) => {
//   return JSON.stringify(serializeDocument(a)) === JSON.stringify(serializeDocument(b));
// };

export const isDocumentDirty = (document?: EditorDocument) => {
  if (!document || !document.contents) return false;
  // just use a basic comparison for now
  const { version, type } = document.document;
  const current = serializeContents(document.contents, version, type);
  // we want to run deserialize and serialize as due to auto conversion, the contents may have changed
  // ideally serialize should be the same as raw saved
  const saved = serializeContents(deserializeDocument(document.document).contents, version, type);
  // can't be dirty if one of them doesn't exist
  if (!current || !saved) return false;

  if (type === "rulesheet") {
    // Special case. When the sheet is first created the backend will store it as `declarations: [], sheets: []`
    // But the front end will have more scaffolding in place to run the UI. If we leave this as dirty then the UI
    // will make it appear that the user changed something, when they didn't. I thought about trying
    // change the serialisation to handle this - but too many edge cases where it could go wrong. Easier to put this in
    // workaround is check if they are both default and if so, not dirty
    if (JSON.stringify(saved) === defaultBackendSheet && JSON.stringify(current) === defaultFrontendSheet) {
      return false;
    }
  }

  return JSON.stringify(current) !== JSON.stringify(saved);
};

const initDocument = (document, localContents?: DeserializedDocumentContents): EditorDocument => {
  // store the original document for reference
  const { contents } = deserializeDocument(document);
  return {
    document,
    // if we have local contents, use them, otherwise use the deserialized contents
    contents: localContents ?? contents,
    errors: document.errors ?? [],
    validations: [],
    lastSaved: null, // don't pre-populate this, we want to know if we have saved
    isDirty: Boolean(localContents), // if we have local contents, we are dirty
    keepLocal: false,
    lastLoadedTime: Date.now(),
    renderKey: 0,
  };
};

const initialState = {} as { [id: string]: EditorDocument };

// interface DocumentAction {
//   id: string;
//   [key: string]: any;
// }

/**
 * document slice structure
 *
 * {
 *  [id]: {
 *   document: {},
 *   contents: [],
 *   errors: [],
 *   validations: [],
 *   lastSaved: null,
 *  }
 * }
 */

const documentsSlice = createSlice({
  name: "documents-new",
  initialState,
  reducers: {
    setDocument: (state, action: PayloadAction<{ id: string; document: BackendResource<Document> }>) => {
      const { id, document } = action.payload;
      const doc = state[id];
      // if we have the internal doc, we are initialised
      const isInitialised = Boolean(doc?.document);
      if (isInitialised) {
        // update the inner document
        state[id].document = document;
        const { contents } = deserializeDocument(document);
        state[id].contents = contents;
        state[id].errors = document.errors ?? [];
        state[id].keepLocal = false; // ensure to reset this value
        state[id].lastLoadedTime = Date.now();
        // do NOT change the render key, as we don't want to always force a re-render
        return;
      }
      // try passing in local contents if it exists
      state[id] = initDocument(document, doc?.contents);
    },
    setSavedDocument: (state, action: PayloadAction<{ id: string; document: BackendResource<Document> }>) => {
      const { id, document } = action.payload;
      const doc = state[id];
      if (!doc) {
        state[id] = initDocument(document);
        return;
      }

      const preservedErrors = doc.errors;
      const savedDocument = applyLocalErrorsToSavedDocument(document, preservedErrors);
      const { contents } = deserializeDocument(savedDocument);
      state[id].document = savedDocument;
      state[id].contents = contents;
      state[id].errors = preservedErrors;
      state[id].keepLocal = false;
      state[id].lastLoadedTime = Date.now();
    },
    setContents: (state, action: PayloadAction<{ id: string; contents: DeserializedDocumentContents }>) => {
      const { id, contents } = action.payload;
      if (!contents[DESERIALIZED_SYMBOL]) {
        console.error("Invalid document contents, missing deserialized symbol", contents);
      }
      state[id].contents = contents;
    },
    setErrors: (state, action: PayloadAction<{ id: string; errors: DocumentError[] }>) => {
      const { id, errors } = action.payload;
      state[id].errors = errors;
    },
    setValidations: (state, action: PayloadAction<{ id: string; validations: any[] }>) => {
      const { id, validations } = action.payload;
      state[id].validations = validations;
    },
    setLastSaved: (state, action: PayloadAction<{ id: string; lastSaved: number }>) => {
      const { id, lastSaved } = action.payload;
      state[id].lastSaved = lastSaved;
    },
    setSource: (state, action: PayloadAction<{ id: string; source: any[] }>) => {
      const { id, source } = action.payload;
      if (Array.isArray(state[id].contents)) return;
      (state[id].contents as RuleDocContentsV2).source = source;
    },
    setRules: (state, action: PayloadAction<{ id: string; rules: any[] }>) => {
      const { id, rules } = action.payload;
      if (Array.isArray(state[id].contents)) return;
      (state[id].contents as RuleDocContentsV2).rules = rules;
    },
    setSections: (state, action: PayloadAction<{ id: string; sections: any[] }>) => {
      const { id, sections } = action.payload;
      if (Array.isArray(state[id].contents)) return;
      (state[id].contents as RuleDocContentsV2).sections = sections;
    },
    setDirty: (state, action: PayloadAction<{ id: string; isDirty: boolean }>) => {
      const { id, isDirty } = action.payload;
      state[id].isDirty = isDirty;
    },
    setKeepLocal: (state, action: PayloadAction<{ id: string; keepLocal: boolean }>) => {
      const { id, keepLocal } = action.payload;
      state[id].keepLocal = keepLocal;
    },
    forceRender: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      const key = state[id].renderKey + 1;
      state[id].renderKey = key;
    },
  },
  // extra reducer that runs post all actions
  extraReducers: (builder) => {
    // after all actions, run an is dirty check
    builder.addMatcher(
      (action) => action.type.startsWith("documents-new") && action.payload?.id && !action.type.endsWith("setDirty"),
      (state, action) => {
        // @ts-ignore
        const document = state[action.payload?.id];
        if (!document) return;
        // parse in/out of JSON as we want a basic object, not a proxy object
        const docJSON = JSON.parse(JSON.stringify(document));
        document.isDirty = isDocumentDirty(docJSON);
      },
    );
  },
});

export const {
  setDocument,
  setSavedDocument,
  setContents,
  setErrors,
  setValidations,
  setLastSaved,
  setKeepLocal,
  setSource,
  setRules,
  setSections,
  forceRender,
} = documentsSlice.actions;

export type EditDocumentOptions = {
  /** @deprecated */
  serialize?: (value: any) => any;
  /** @deprecated */
  deserialize?: (value: any) => any;
  validate?: (value: DeepPartial<DocumentContents>) => DocumentError[];
};

export const useSections = (documentId: string) => {
  const dispatch = useDispatch();
  const sections = useSelector(
    (state) => state[documentsSlice.name][documentId]?.contents.sections ?? [],
  ) as SectionData[];
  const { setSections } = documentsSlice.actions;

  return {
    sections,
    setSections: (newSections: SectionData[]) => dispatch(setSections({ id: documentId, sections: newSections })),
    addSection: (section: SectionData) => dispatch(setSections({ id: documentId, sections: [...sections, section] })),
    getSection: (id: string) => sections.find((section) => section.id === id),
    setSection: (id: string, section: Partial<SectionData>) => {
      const index = sections.findIndex((s) => s.id === id);
      if (index === -1) throw new Error(`Section with id ${id} not found`);
      const oldSection = sections[index];
      const newData = [...sections];
      newData[index] = { ...oldSection, ...section };
      dispatch(setSections({ id: documentId, sections: newData }));
    },
    removeSection: (id: string) => {
      const index = sections.findIndex((s) => s.id === id);
      if (index === -1) throw new Error(`Section with id ${id} not found`);
      const newData = [...sections];
      newData.splice(index, 1);
      dispatch(setSections({ id: documentId, sections: newData }));
    },
  };
};

const useSaveDocument = (options: EditDocumentOptions = {}) => {
  const dispatch = useDispatch();
  const { mutateAsync, isLoading } = documentService.useUpdate();
  const { validate } = options;

  const { setValidations, setLastSaved, setSavedDocument } = documentsSlice.actions;

  const onSave = async (document: EditorDocument) => {
    if (!document) return; // nothing to save
    const { id } = document.document;
    if (!id) return;
    try {
      console.log("Saving document:", document);
      type Contents = ReturnType<typeof serializeDocument>["contents"];
      // // Convert slate state into backend
      const contents = ((): Contents => {
        const { contents } = serializeDocument({ ...document.document, contents: document.contents } as any);
        const { document: doc } = document;
        if (doc.type !== "datatable") return contents;

        const headers: string[] = get(contents, "headers", []);
        const rows = cleanupJSpreadsheetData(get(contents, "rows", []), headers.length);

        return { rows, headers } as unknown as Contents;
      })();
      const validations = (contents && validate?.(contents)) ?? [];
      dispatch(setValidations({ id, validations }));
      console.log("Dispatching update:", contents);
      // document version update code, move all v2 to v3, leave v1 as is
      const version = document.document.version && document.document.version > 1 ? 3 : 1;
      // @ts-ignore TODO This is where I hate TS being too strict and over typing - not my problem right now
      const res = await mutateAsync({ id, payload: { contents, version } as Partial<Document> });
      console.log("save document response", res);
      // update the document in redux
      // @ts-ignore
      dispatch(setSavedDocument({ id, document: res }));
      // update release as graph should have changed
      // TODO we want this to auto update using sockets
      dispatch(crudGetOne("releases", document.document.release));
      dispatch(fullReleaseRedux.aCreators.requestToActualize());
      // update last saved on success
      dispatch(setLastSaved({ id, lastSaved: Date.now() }));
      return true;
    } catch (err) {
      console.error("Error saving document", err);
      return false;
    }
  };

  return {
    onSave,
    saving: isLoading,
  };
};

export const useModifyDocument = (options: EditDocumentOptions = {}) => {
  const { document } = useRuleDocument();
  // const documentState = useSelector((state) => state[documentsSlice.name][document.id]) as EditorDocument | undefined;
  const { onSave, saving } = useSaveDocument(options);

  const saveDocument = async () => {
    if (!document || saving) return; // nothing to save
    console.log("Saving document:", document);
    await onSave(document);
  };

  return {
    saveDocument,
    saving,
  };
};

export const useDocumentActions = (id: string) => {
  const dispatch = useDispatch();
  const actions = documentsSlice.actions;

  return {
    setDocument: (document: BackendResource<Document>) => dispatch(actions.setDocument({ id, document })),
    setContents: (contents: DeserializedDocumentContents) => dispatch(actions.setContents({ id, contents })),
    setErrors: (errors: DocumentError[]) => dispatch(actions.setErrors({ id, errors })),
    setValidations: (validations: DocumentError[]) => dispatch(actions.setValidations({ id, validations })),
    setLastSaved: (lastSaved: number) => dispatch(actions.setLastSaved({ id, lastSaved })),
    setSource: (source: any[]) => dispatch(actions.setSource({ id, source })),
    setRules: (rules: any[]) => dispatch(actions.setRules({ id, rules })),
    setSections: (sections: any[]) => dispatch(actions.setSections({ id, sections })),
    forceRender: () => dispatch(actions.forceRender({ id })),
  };
};

/** @deprecated this causes too many re-renders, use the new context and hooks */
export const useEditDocument = (id: string, options: EditDocumentOptions = {}) => {
  const dispatch = useDispatch();
  const notify = useNotify();
  const actions = useDocumentActions(id);

  const [update, setUpdate] = useState(false);

  const { data: rawDocument } = documentService.useGetOne(id, {
    // refetch every 25 seconds to ensure we keep the editor alive
    // editor timeout is 30 seconds, so this should be enough
    refetchInterval: 25000,
    onError(err) {
      // TODO convert this to a notify
      console.error("Error fetching document", err);
      notify("Document not found", "error");
    },
    onSuccess(data) {
      console.log("Loaded document", data);
      // const doc = deserializeDocument(data);
      // dispatch(setDocument({ id, document: doc }));
      setUpdate(true);
    },
  });

  const document = useSelector((state) => state[documentsSlice.name][id]) as EditorDocument | undefined;
  const { onSave, saving } = useSaveDocument(options);

  useEffect(() => {
    if (update) {
      // a new document was loaded from the BE
      // console.log("document changed", document, rawDocument);
      if (!document?.document && rawDocument) {
        console.log("raw doc only, so should be init", document, rawDocument);
        // initial document load

        dispatch(setDocument({ id, document: rawDocument }));
        setUpdate(false);

        /*if (options.validate) {
          const validations = (rawDocument.contents && options.validate?.(rawDocument.contents)) ?? [];
          dispatch(setValidations({ id, validations }));
        }*/
        return;
      }

      // else we need to check if we can update or have a merge issue
      if (document && rawDocument) {
        // we want to compare edit times
        const lastSaved = document.document.lastModified;
        // console.log("compare docs", document.document, rawDocument);
        // determine if the BE has updated and we are now too old
        const hasUpdated =
          lastSaved &&
          (!rawDocument.lastModified || new Date(lastSaved).getTime() < new Date(rawDocument.lastModified).getTime());
        // console.log("document update timestamps", new Date(lastSaved), new Date(rawDocument.lastModified), hasUpdated);
        // get if the user has local changes
        const isDirty = document.isDirty;
        if (hasUpdated && !isDirty) {
          // console.log("Document was updated, changing local copy");
          dispatch(setDocument({ id, document: rawDocument }));
          // if (doc.version === 2) {
          //   dispatch(setRules({ id, rules: (doc.contents as Contents).rules }));
          // }
          setUpdate(false);
          return;
        }
        if (hasUpdated && isDirty) {
          // we now have a merge issue, only update errors
          dispatch(
            setDocument({
              id,
              document: {
                ...document.document,
                // @ts-ignore
                errors: rawDocument.errors,
              },
            }),
          );
          setUpdate(false);
          return;
        }
      }
    }
  }, [update, rawDocument, document]);

  const saveDocument = async () => {
    if (!document || saving) return; // nothing to save

    console.log("Saving document:", document);
    // setSaving(true);
    await onSave(document);
    // setSaving(false);
  };

  return {
    document,
    isDirty: document?.isDirty ?? false, // easy util
    saving,
    saveDocument,
    ...actions,
  };
};

// a react context for the document, it gets given the document id
// then loads the document using documentService.useGetOne
// then stores the document in the redux store
// the context holds the document and provides the actions

export type IRuleDocumentContext = {
  id: string;
  /** NOTE this contents is deserialized */
  document: EditorDocument; // BackendResource<Document>;
} & ReturnType<typeof useDocumentActions>;

export const RuleDocumentContext = createContext<IRuleDocumentContext>(null as unknown as IRuleDocumentContext);

const useDocumentUpdates = (id: string, rawDocument: BackendResource<Document> | undefined) => {
  const dispatch = useDispatch();
  const document = useSelector((state) => state[documentsSlice.name][id]) as EditorDocument | undefined;

  useEffect(() => {
    // a new document was loaded from the BE
    if (!document?.document && rawDocument) {
      console.log("raw doc only, so should be init", document, rawDocument);
      // initial document load
      dispatch(setDocument({ id, document: rawDocument }));
      return;
    }

    // else we need to check if we can update or have a merge issue
    if (document && rawDocument) {
      // we want to compare edit times
      const lastSaved = document.document.lastModified;
      // determine if the BE has updated and we are now too old
      // hasUpdate if internal stored document is older than latest received from BE
      const hasUpdated =
        lastSaved &&
        (!rawDocument.lastModified || new Date(lastSaved).getTime() < new Date(rawDocument.lastModified).getTime());

      // get if the user has local changes or has already stated they want to keep local
      const { isDirty, keepLocal } = document;
      if (hasUpdated && !isDirty) {
        console.log("Document was updated, changing local copy");
        dispatch(setDocument({ id, document: rawDocument }));
        return;
      }
      if (hasUpdated && isDirty && !keepLocal) {
        console.log("Document was updated, but local is dirty");
        // TODO come back to this later, its causing too much confusion
        /*
        dispatch(
          legacyConfirmation({
            title: "Document updated",
            onDismiss: () => {
              // debugger;
              dispatch(setKeepLocal({ id, keepLocal: true }));
            },
            body: (
              <Typography>
                The document has been updated by another user. Would you like to:
                <ul>
                  <li>
                    Keep <strong>Mine</strong>. Allowing you to continue working on your changes.
                    Note saving after this will override their changes.
                  </li>
                  <li>
                    Use <strong>Theirs</strong>? Losing your current changes.
                  </li>
                  <li>
                    <strong>Merge</strong> the document? A new window will open with the latest changes.
                    Please update and save the document in the new window.
                  </li>
                </ul>
              </Typography>
            ),
            actions: (close) => ({
              primary:
              {
                name: "Merge",
                onClick: () => {
                  // make sure we keep the local and stop prompting
                  dispatch(setKeepLocal({ id, keepLocal: true }));
                  window.open(`/doc/${id}`, "_blank", "scrollbars=yes,resizable=yes,top=500,left=500,width=1280,height=720");
                  close();
                }
              },
              secondary: [
                {
                  name: "Mine",
                  onClick: () => {
                    dispatch(setKeepLocal({ id, keepLocal: true }));
                    close();
                  },
                },
                {
                  name: "Theirs",
                  onClick: () => {
                    dispatch(setDocument({ id, document: rawDocument }));
                    close();
                  },
                },
              ]
            }),
          })
        );
        */
        return;
      }
    }
  }, [rawDocument, document]);

  return; // return nothing
};

const cloneParsedGraph = (graph: ParsedRuleGraph): ParsedRuleGraph => {
  return graphlib.json.read(graphlib.json.write(graph)) as ParsedRuleGraph;
};

interface CompileLocalRuleDocumentOptions {
  document: EditorDocument;
  parsedGraph: ParsedRuleGraph;
  release: Release;
  enableNamespaces: boolean;
}

const compileLocalRuleDocument = ({
  document,
  parsedGraph,
  release,
  enableNamespaces,
}: CompileLocalRuleDocumentOptions) => {
  const { version, type } = document.document;
  const contents = serializeContents(document.contents, version, type);

  return compile({
    currentDocument: {
      ...document.document,
      contents,
    } as Document,
    graph: cloneParsedGraph(parsedGraph),
    relationships: release.relationships,
    documentTree: release.documents,
    enableNamespaces,
  });
};

export const useLiveDocumentCompiler = (id: string) => {
  const dispatch = useDispatch();
  const document = useSelector((state) => state[documentsSlice.name][id]) as EditorDocument | undefined;
  const parsedGraph = useParsedGraph();
  const [liveGraph, setLiveGraph] = useState<ParsedRuleGraph>(parsedGraph);
  const release = useFullRelease();
  const project = useCurrentProject();

  const compileDocument = useDebouncedCallback(() => {
    if (!document?.isDirty || !document.document || !release || !parsedGraph || document.document.type !== "ruledoc") {
      setLiveGraph(parsedGraph);
      return;
    }

    try {
      const result = compileLocalRuleDocument({
        document,
        parsedGraph,
        release,
        enableNamespaces: Boolean(project?.name?.includes(NAMESPACE_OPT_IN)),
      });
      result.graph.id = uuid();
      setLiveGraph(result.graph);
      dispatch(setErrors({ id, errors: result.errors }));
    } catch (error) {
      console.error("Error compiling live document preview", error);
      setLiveGraph(parsedGraph);
      dispatch(setErrors({ id, errors: [wrapTechnicalCompilerError(error)] }));
    }
  }, 1000);

  useEffect(() => {
    compileDocument();
  }, [document]);

  return {
    liveGraph,
  };
};

export const useLocalDocumentCompiler = (id: string) => {
  const dispatch = useDispatch();
  const notify = useNotify();
  const document = useSelector((state) => state[documentsSlice.name][id]) as EditorDocument | undefined;
  const parsedGraph = useParsedGraph();
  const release = useFullRelease();
  const project = useCurrentProject();

  const compileDocument = () => {
    if (!document?.document || !release || !parsedGraph) {
      notify("Document check is not ready yet", "warning");
      return;
    }
    if (document.document.type !== "ruledoc") {
      return;
    }

    try {
      const result = compileLocalRuleDocument({
        document,
        parsedGraph,
        release,
        enableNamespaces: Boolean(project?.name?.includes(NAMESPACE_OPT_IN)),
      });

      dispatch(setErrors({ id, errors: result.errors }));
      if (result.errors.length === 0) {
        notify("Document check passed", "info");
      } else {
        notify(
          `Document check found ${result.errors.length} ${result.errors.length === 1 ? "issue" : "issues"}`,
          "warning",
        );
      }
    } catch (error: any) {
      dispatch(
        setErrors({
          id,
          errors: [wrapTechnicalCompilerError(error)],
        }),
      );
      notify("Document check failed", "error");
    }
  };

  return {
    compileDocument,
  };
};

export const RuleDocumentProvider = ({ id, children }) => {
  const notify = useNotify();
  const actions = useDocumentActions(id);

  const { data: document } = documentService.useGetOne(id, {
    // refetch every 25 seconds to ensure we keep the editor alive
    // editor timeout is 30 seconds, so this should be enough
    refetchInterval: 25000,
    onError(err) {
      console.error("Error fetching document", err);
      notify("Document not found", "error");
    },
  });

  // listen for updates but do not return anything as we don't want to re-render
  useDocumentUpdates(id, document);

  // const internalDocument = useSelector((state) => state[documentsSlice.name][id]?.document) as BackendResource<Document> | undefined;
  // deserialize as we want to use it for the initial plate values
  // const deserialized = internalDocument ? deserializeDocument(internalDocument) : undefined;
  const reduxDocument = useSelector((state) => state[documentsSlice.name][id]) as EditorDocument | undefined;

  const context = {
    id,
    // document: deserialized,
    document: reduxDocument,
    ...actions,
  } as IRuleDocumentContext;

  return <RuleDocumentContext.Provider value={context}>{children}</RuleDocumentContext.Provider>;
};

export const useRuleDocument = () => {
  const context = useContext(RuleDocumentContext);
  if (!context) {
    console.trace();
    throw new Error("useRuleDocument must be used within a RuleDocumentProvider");
  }
  return context;
};

export const newDocumentsName = documentsSlice.name;

export const useNewDocuments = () =>
  useSelector((state) => state[documentsSlice.name]) as Record<string, EditorDocument>;

export const useNewDocumentSelector = (selector: (documents: Record<string, EditorDocument>) => any) =>
  useSelector((state) => selector(state[documentsSlice.name]));

/**
 * A hook to get the document being currently edited. Useful for other elements of the system.
 * If you are within a document context, try using useRuleDocument to get the latest document instead.
 */
export const useCurrentDocument = () => {
  // @ts-ignore - documents exists, but we have not typed redux
  const currentDocument = useSelector((state) => state.documents?.document);
  const editorDocument = useSelector(
    (state) => state[documentsSlice.name][currentDocument?.reference] ?? null,
  ) as EditorDocument | null;
  if (!currentDocument || !editorDocument) return null;
  return {
    ...currentDocument,
    ...editorDocument.document,
  } as Document;
};

export const useIsDocumentDirty = (id?: string) =>
  useSelector((state) => {
    if (!id) return false;
    const doc = state[documentsSlice.name][id];
    return doc?.isDirty ?? false;
  });

export const useDirtyDocuments = (options: EditDocumentOptions = {}) =>
  useSelector((state) => {
    const { onSave } = useSaveDocument(options);
    const [saving, setSaving] = useState(false);
    const docs = state[documentsSlice.name] as Record<string, EditorDocument>;
    const dirty = Object.values(docs).filter((doc) => doc.isDirty);

    const saveAll = async () => {
      if (saving) return;
      setSaving(true);
      const res = await Promise.allSettled(dirty.map(onSave));
      console.log("save all results", res);
      setSaving(false);
    };

    return {
      documents: docs,
      total: dirty.length,
      saving,
      saveAll,
    };
  });

export const useDocumentErrors = (id: string) => {
  const dispatch = useDispatch();
  const doc = useSelector((s) => s[documentsSlice.name][id]);

  const { errors, validations } = doc ?? { errors: [], validations: [] };

  return {
    errors,
    validations,
    hasErrors: errors?.length > 0 || validations?.length > 0,
    setErrors: (errs) => dispatch(setErrors({ id, errors: errs })),
    setValidations: (errs) => dispatch(setValidations({ id, validations: errs })),
  };
};

/** get all compile errors in the project */
export const useCompileErrors = () => {
  // const dispatch = useDispatch();
  const release = useFullRelease();
  const documents = release?.documents ?? [];
  const errors = flattenReleaseDocuments(documents).map((doc) => doc.errors);
  return errors;
};

/** get all compile errors for a given document */
export const useDocumentCompileErrors = (id: string) => {
  const release = useFullRelease();
  const documents = release?.documents ?? [];
  const errors = findReleaseDocument(documents, (doc) => doc.reference === id)?.errors ?? [];
  return errors;
};

export const useRevertDocument = () => {
  const dispatch = useDispatch();
  const notify = useNotify();
  const { pushConfirmDialog } = useConfirmDialog();

  return (id: string) => {
    pushConfirmDialog({
      layer: WORKSPACE_LAYER,
      title: "Revert document",
      description: "Are you sure you want to revert the document? All changes will be lost.",
      confirmLabel: "Revert",
      confirmIntent: "warning",
      onConfirm: async () => {
        try {
          const res = await documentService.getOne(id);
          dispatch(setDocument({ id, document: res }));
          dispatch(forceRender({ id }));
        } catch (e) {
          console.error("Error reverting document", e);
          notify.error("Error reverting document");
        }
      },
    });
  };
};

export const useLockDocument = () => {
  const { t } = useTranslation();
  const release = useFullRelease();
  const { lockFile } = useLockFile();
  const { pushDialog } = useDialog();

  return (id: string) => {
    if (!release) return;
    const doc = findReleaseDocument(release.documents, (doc) => doc.reference === id);
    if (!doc || !doc.reference) return;
    const lockedByMe = isLockedByMe(doc.locked);
    const lockedByOther = isLockedByOther(doc.locked);
    pushDialog({
      layer: WORKSPACE_LAYER,
      options: { intent: "form" },
      component: ({ close }) => (
        <Dialog.Content className="flex flex-col">
          <DialogHeader
            title={t("are_you_sure")}
            description={
              lockedByMe
                ? t("documents.locking_by_you")
                : lockedByOther
                  ? t("documents.unlocking_confirmation", { user: doc.locked!.full_name })
                  : t("documents.locking_confirmation")
            }
          />
          <Dialog.Actions>
            {!lockedByMe && (
              <Dialog.Actions.SecondaryButton
                label={t("documents.unlock_action")}
                disabled={!lockedByOther}
                onClick={() => {
                  lockFile(id, false);
                  close();
                }}
              >
                {t("documents.unlock_action")}
              </Dialog.Actions.SecondaryButton>
            )}
            {lockedByMe ? (
              <Dialog.Actions.PrimaryButton
                label={t("documents.unlock_action")}
                onClick={() => {
                  lockFile(id, false);
                  close();
                }}
              >
                {t("documents.unlock_action")}
              </Dialog.Actions.PrimaryButton>
            ) : (
              <Dialog.Actions.PrimaryButton
                label={lockedByOther ? t("documents.lock_locked_action") : t("documents.lock_unlocked_action")}
                intent={lockedByOther ? "destructive" : "primary"}
                onClick={() => {
                  lockFile(id, true);
                  close();
                }}
              >
                {lockedByOther ? t("documents.lock_locked_action") : t("documents.lock_unlocked_action")}
              </Dialog.Actions.PrimaryButton>
            )}
          </Dialog.Actions>
        </Dialog.Content>
      ),
    });
  };
};

const cleanElement = (element) => {
  if (Array.isArray(element.children)) {
    element.children = element.children.reduce((children, child) => {
      if (child.type !== "mention_input") {
        children.push(cleanElement(child));
      }
      return children;
    }, []);
  }
  return element;
};

// we only want to persist documents that are dirty
const persistTransform = createTransform(
  (inboundState, key) => {
    // always store the persist data
    if (key === "_persist") return inboundState;
    const doc = inboundState as EditorDocument;
    // console.log("[PersistTransform] Inbound state", key, doc);
    if (doc.isDirty) {
      // just persist the users contents and isDirty flag (that way we can see its dirty without having to open it)
      // also persist the timestamp in case we want to reference this
      // could be useful later when checking / informing the user they are behind
      // @ts-expect-error
      const rules = doc.contents?.rules;
      /*if (Array.isArray(rules)) {
        // @ts-expect-error
        doc.contents.rules = rules.map(cleanElement);
      }*/

      return { contents: doc.contents, isDirty: true, timestamp: Date.now() };
    }
    return undefined;
  },
  (outboundState, key) => {
    // console.log("[PersistTransform] Outbound state", key, outboundState);
    return outboundState;
  },
);

const persistedReducer = persistReducer(
  {
    key: "immi.documents",
    storage,
    transforms: [persistTransform],
  },
  documentsSlice.reducer,
);

export default persistedReducer;
